import AsyncStorage from '@react-native-async-storage/async-storage';
import { Logger } from './logger';

// Small standalone cache for base64-image API responses (gallery images, event
// images, etc. — anything returned as { imageBase64 } rather than a static URL).
// In-memory first for the current session, AsyncStorage as a cross-session layer
// so images already viewed don't get re-downloaded/re-decoded on next launch.
const memoryCache = new Map<string, string>();

const storageKey = (namespace: string, id: string) => `imgcache:${namespace}:${id}`;

export async function getCachedImage(namespace: string, id: string): Promise<string | null> {
  const memHit = memoryCache.get(storageKey(namespace, id));
  if (memHit) return memHit;

  try {
    const stored = await AsyncStorage.getItem(storageKey(namespace, id));
    if (stored) {
      memoryCache.set(storageKey(namespace, id), stored);
      return stored;
    }
  } catch (err) {
    Logger.warn('ImageCache', `Failed to read cache for ${namespace}/${id}`, err);
  }
  return null;
}

export async function setCachedImage(namespace: string, id: string, dataUri: string): Promise<void> {
  memoryCache.set(storageKey(namespace, id), dataUri);
  try {
    await AsyncStorage.setItem(storageKey(namespace, id), dataUri);
  } catch (err) {
    Logger.warn('ImageCache', `Failed to persist cache for ${namespace}/${id}`, err);
  }
}

const BASE64_SIGNATURES: Array<[string, string]> = [
  ['/9j/', 'image/jpeg'],
  ['iVBOR', 'image/png'],
  ['R0lGOD', 'image/gif'],
  ['UklGR', 'image/webp'],
];

export function detectImageContentType(value: string): string {
  const match = BASE64_SIGNATURES.find(([prefix]) => value.startsWith(prefix));
  return match ? match[1] : 'image/jpeg';
}

export function toBase64DataUri(base64: string): string {
  return `data:${detectImageContentType(base64)};base64,${base64}`;
}
